import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useViewer } from '@treams/treams-hooks';
import { ArrowLeftIcon, VisibilityOffIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Author,
	Button,
	Container,
	Heading,
	IconWrapper,
	NumberedSubheading,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	QuillText,
	Skeleton,
	Subheading,
	TextBubble,
	Title,
	Toolbar,
} from '@treams/treams-ui';

import VoiceMemoPlayback from '../../packages/@treams/treams-app/src/components/voice-memos/VoiceMemoPlayback';
import { CommentsWithData } from '../comments/Comments';
import CommentBar from '../comments/components/CommentBar';
import GoalCard from '../goals/GoalCard';
import CompetencyDisplay from '../packages/kakapo-ui-application/common/containers/CompetencyDisplay';
import { FeedbackQuestionMoreInformation } from './FeedbackQuestionMoreInformation';
import { feedbackQuestionLevels } from './feedbackQuestionLevels';

const Divider = styled(Box)`
	height: 1px;
	width: 100%;
	background: ${(props) => props.theme.tokens.border.subtle};
`;

function OutgoingFeedbackAnswer(props: {
	recipient: graphql.User;
	response: graphql.FeedbackResponse;
	question: graphql.FeedbackRequestQuestion;
	answer: graphql.FeedbackResponseAnswer;
	index: number;
}) {
	const { localize } = useLocalize();
	const [composer, setComposer] = useState(false);
	const { t } = useTranslation();

	const metadata = { questionID: props.question.id };

	const selectedOption = props.answer.selectedOptionID
		? props.question.options?.find(({ id }) => id === props.answer.selectedOptionID)
		: undefined;

	return (
		<Box mt={5}>
			<NumberedSubheading index={props.index}>
				<Heading>{props.question.text}</Heading>
				{props.question.subTitle && (
					<Paragraph mt={2} mb={0}>
						{props.question.subTitle}
					</Paragraph>
				)}
				{!QuillText.isEmpty(props.question.additionalDetails) && (
					<FeedbackQuestionMoreInformation additionalDetails={props.question.additionalDetails} />
				)}
			</NumberedSubheading>
			{props.question.canSubjectView === false && (
				<Flex mb={2} alignItems="center">
					<IconWrapper mr={1} size={16}>
						<VisibilityOffIcon />
					</IconWrapper>
					<Paragraph mb={0}>{t('feedback.answer.hiddenFromEmployee.note')}</Paragraph>
				</Flex>
			)}
			{props.question.competency && (
				<Subheading mb={2}>{localize(props.question.competency.title).text}</Subheading>
			)}
			<Box mb={3}>
				{props.question.goal && (
					<GoalCard small goal={props.question.goal as unknown as graphql.Goal} />
				)}
			</Box>
			<Box mb={3}>
				<CommentBar.Container
					onComment={() => setComposer((composer) => !composer)}
					targetID={props.response.id}
					targetType={graphql.CommentTargetType.FeedbackResponse}
					metadata={metadata}
				>
					<TextBubble
						user={props.recipient}
						text={props.answer.text}
						stars={props.answer.stars ?? undefined}
					>
						{props.question.competency && typeof props.answer.competencyLevelIndex === 'number' && (
							<Box mt={2} width="100%">
								<CompetencyDisplay
									levels={feedbackQuestionLevels(props.question)}
									manager={props.answer.competencyLevelIndex}
								/>
							</Box>
						)}
						{props.question.competency && props.answer.competencyLevelIndex === null && (
							<Box mt={2}>
								<Paragraph fontWeight="bold">{t('feedback.answer.not.observed')}</Paragraph>
							</Box>
						)}
						{selectedOption && (
							<Box mt={2}>
								<Paragraph fontWeight="bold">{localize(selectedOption.text).text}</Paragraph>
							</Box>
						)}
						{props.answer.voiceMemo && (
							<Box py={3}>
								<VoiceMemoPlayback
									samples={props.answer.voiceMemo.samples}
									source={props.answer.voiceMemo.asset.source.url}
								/>
							</Box>
						)}
					</TextBubble>
				</CommentBar.Container>
			</Box>
			<Box ml={4}>
				<CommentsWithData
					empty={false}
					composer={composer}
					target={props.response}
					comments={props.response.comments}
					autoFocus
					metadata={metadata}
					onRefetch={() => {}}
					onCancelComposer={() => setComposer(false)}
				/>
			</Box>
		</Box>
	);
}

export default function OutgoingFeedback() {
	const { t } = useTranslation();
	const viewer = useViewer();

	const { params } = useRouteMatch<{ userID?: string; id: string }>();
	const { userID, id } = params;

	const query = graphql.useInternalFeedbackQuery({ variables: { id } });

	const feedback = query.data?.internalFeedback;
	const isRecipient =
		!!viewer &&
		!!feedback &&
		feedback.recipient.__typename === 'User' &&
		feedback.recipient.id === viewer.id;
	// Respondents can edit their shared feedback until the request's release
	// date, i.e. as long as their answers aren't visible to the subject yet.
	const canEdit =
		isRecipient &&
		feedback?.status === graphql.FeedbackResponseStatus.Shared &&
		!!feedback.releaseDate &&
		new Date(feedback.releaseDate).getTime() > Date.now();

	return (
		<Container py={4} wide={PAGE_CONTENT_WIDTH}>
			<Toolbar>
				<Button
					icon={<ArrowLeftIcon />}
					link={userID ? `/users/${userID}/feedback/given` : '/feedback/given'}
					title={t('goals.show.toolbar.back')}
					type="tertiary"
					ml="-12px"
				/>
				{canEdit && (
					<Button
						link={`/users/${feedback.requestor.id}/feedback/${id}/respond`}
						title={t('feedback.answer.edit')}
					/>
				)}
			</Toolbar>

			<Box mt={4}>
				{query.data ? (
					<>
						<Title mb={3}>
							{query.data.internalFeedback.title ||
								t('feedback.outgoing.detail.title', {
									name: query.data.internalFeedback.requestor.firstName,
								})}
						</Title>
						<Author
							user={query.data.internalFeedback.requestor as graphql.User}
							description={t('feedback.outgoing.detail.createdAt')}
							createdAt={query.data.internalFeedback.sharedAt}
						/>
						<Divider mt={5} mb={4} />

						<Box>
							{query.data.internalFeedback.answers?.map((answer, index) => {
								const question = query.data?.internalFeedback.questions[index]!;

								return (
									<OutgoingFeedbackAnswer
										key={answer.questionID}
										index={index + 1}
										response={query.data?.internalFeedback as graphql.FeedbackResponse}
										answer={answer as graphql.FeedbackResponseAnswer}
										question={question as graphql.FeedbackRequestQuestion}
										recipient={query.data?.internalFeedback.recipient as graphql.User}
									/>
								);
							})}
						</Box>
					</>
				) : (
					<>
						<Title.Skeleton mb={1} />
						<Heading.Skeleton mb={3} />
						<Author.Skeleton />

						<Box width={[1, 1, 9 / 12]}>
							<Skeleton.Repeat count={3}>
								<Box mt={4}>
									<Subheading.Skeleton mb={2} />
									<TextBubble.Skeleton />
								</Box>
							</Skeleton.Repeat>
						</Box>
					</>
				)}
			</Box>
		</Container>
	);
}
